import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import VideoLightbox from '../../components/common/VideoLightbox';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import {
  Eyebrow, SectionHeading, IconBadge, ContextStrip, InfoCard,
  GradientButton, CTABanner, VideoCard,
} from '../../components/common/Primitives';
import { PARTNERS_OVERVIEW as P } from '../../lib/mockPartners';
import { PlayCircle, ArrowRight } from 'lucide-react';

const PARTNER_DEPLOY_LEARN_IMAGE = 'https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/tj65yri8_Others_overview.jpeg';

export default function PartnersOverview() {
  const [showcaseOpen, setShowcaseOpen] = useState(false);
  return (
    <PageShell>
      {/* ================ HERO ================ */}
      <section className="px-6 pt-12 pb-8" data-testid="partners-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] items-start">
            <div>
              <Eyebrow color="purple">{P.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.06] tracking-tight text-navy">{P.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{P.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={P.hero.ctaPrimary.to} data-testid="partners-hero-cta-primary">{P.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={P.hero.ctaSecondary.to} variant="outline" data-testid="partners-hero-cta-secondary">{P.hero.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-4 shadow-soft">
              <div className="text-center text-[11.5px] font-bold tracking-[0.14em] text-purple">{P.hero.imageLabel}</div>
              <div className="mt-3 relative aspect-[4/3] rounded-2xl overflow-hidden bg-black">
                <img
                  src={PARTNER_DEPLOY_LEARN_IMAGE}
                  alt={P.hero.imageLabel}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="partners-hero-image"
                />
              </div>
            </div>
          </div>
          <div className="mt-10"><ContextStrip items={P.strip} /></div>
        </div>
      </section>

      {/* ================ ONE COMPLETE LEARNING ECOSYSTEM ================ */}
      <section className="px-6 pt-4 pb-8" data-testid="partners-ecosystem-4pillars">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl border border-canvas-200 bg-white p-6 md:p-7 shadow-soft">
            <div>
              <div className="text-[11.5px] font-bold tracking-widest text-purple">ONE COMPLETE LEARNING ECOSYSTEM</div>
              <div className="mt-1 text-[19px] md:text-[22px] font-extrabold text-navy leading-snug">VR Experiences · Web Learning Hub · AI Learning · Analytics</div>
              <p className="mt-1 max-w-2xl text-[13px] text-ink-500">Four connected layers work together to help every learner understand, practise, ask and progress.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'VR Experiences',   desc: 'Immersive Grade 8–9 modules for Science and Mathematics.',                              color: 'purple', icon: 'Headset' },
                { title: 'Web Learning Hub', desc: 'Chapter videos, resources and unlimited quiz practice.',                                color: 'royal',  icon: 'Monitor' },
                { title: 'AI Learning',      desc: 'Gyan Mitra answers questions verbally in English and Hindi.',                          color: 'cyan',   icon: 'Bot' },
                { title: 'Analytics',        desc: 'Time spent, chapter progress and conceptual, applied and theoretical scores.',          color: 'mint',   icon: 'BarChart3' },
              ].map((p2, i) => (
                <div key={i} className="relative rounded-2xl border border-canvas-200 bg-canvas-100 p-4 transition-transform hover:-translate-y-0.5 hover:shadow-soft">
                  <IconBadge icon={p2.icon} color={p2.color} />
                  <div className="mt-3 text-[13.5px] font-bold text-navy">{p2.title}</div>
                  <p className="mt-1 text-[12px] leading-snug text-ink-500">{p2.desc}</p>
                  {i < 3 && (
                    <span className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-canvas-200 text-purple">
                      <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================ SEE IT IN ACTION — programme showcase ================ */}
      <section className="px-6 py-12" data-testid="partners-showcase">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl border border-canvas-200 bg-white p-4 md:p-6 shadow-soft">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr] items-center">
              <button
                type="button"
                onClick={() => setShowcaseOpen(true)}
                aria-label="Play programme video"
                data-testid="partners-showcase-play"
                className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img
                  src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')}
                  alt="A curriculum-aligned VR programme, in real classrooms"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 text-navy shadow-card play-glow group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-9 w-9" />
                  </span>
                </span>
                <div className="absolute left-4 top-4 inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-navy backdrop-blur">Programme film</div>
                <div className="absolute bottom-4 left-4 right-4 text-[12px] text-white/95 drop-shadow">Schools · students · teachers · logistics · operating model</div>
              </button>
              <div>
                <div className="text-[11.5px] font-bold tracking-[0.14em] text-purple">SEE IT IN ACTION</div>
                <h3 className="mt-2 text-[24px] md:text-[28px] font-extrabold text-navy leading-tight">A curriculum-aligned VR programme, in real classrooms</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-ink-500">Watch how Grade 8–9 learners engage with immersive Science and Mathematics modules in offline-first settings, supported by trained coordinators, the web learning hub and Gyan Mitra.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <GradientButton to="/partners/models" data-testid="partners-showcase-cta">Explore Partnership Models</GradientButton>
                  <GradientButton to="/partnership-request" variant="outline" data-testid="partners-showcase-cta2">Discuss Partnership</GradientButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================ WHO CAN PARTNER ================ */}
      <section className="px-6 py-16" data-testid="partners-who">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.who.eyebrow} title={P.who.title} desc={P.who.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {P.who.items.map((it, i) => (
              <InfoCard key={i} icon={it.icon} color={it.color} title={it.title} desc={it.desc} />
            ))}
          </div>
        </div>
      </section>

      {/* ================ WHY GYAN DHARA ================ */}
      <section className="px-6 py-16 bg-white" data-testid="partners-why">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.why.eyebrow} title={P.why.title} desc={P.why.desc} />
          <div className="mt-8 rounded-3xl bg-gradient-to-br from-purple-50 via-white to-mint-50 border border-canvas-200 p-6 md:p-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {P.why.items.map((w, i) => (
                <div key={i}>
                  <IconBadge icon={w.icon} color={w.color} />
                  <div className="mt-3 text-[14px] font-bold text-navy">{w.title}</div>
                  <p className="mt-1 text-[13px] leading-relaxed text-ink-500">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================ PARTNERSHIP MODELS ================ */}
      <section className="px-6 py-16" data-testid="partners-models-preview">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.pathways.eyebrow} title={P.pathways.title} desc={P.pathways.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {P.pathways.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-${p.color} text-white text-[13px] font-extrabold`}>{p.n}</span>
                <div className="mt-3 text-[15px] font-bold text-navy leading-snug">{p.title}</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-start">
            <GradientButton to="/partners/models" data-testid="partners-view-all-models">Explore all four models</GradientButton>
          </div>
        </div>
      </section>

      {/* ================ ECOSYSTEM ================ */}
      <section className="px-6 py-16 bg-white" data-testid="partners-ecosystem">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.ecosystem.eyebrow} title={P.ecosystem.title} desc={P.ecosystem.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {P.ecosystem.items.map((e, i) => (
              <InfoCard key={i} icon={e.icon} color={e.color} title={e.title} desc={e.desc} />
            ))}
          </div>
        </div>
      </section>

      {/* ================ CURRENT IMPLEMENTATION CONTEXT ================ */}
      <section className="px-6 py-16" data-testid="partners-context">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.context.eyebrow} title={P.context.title} desc={P.context.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
            <div className="rounded-3xl bg-navy p-8 text-white shadow-card">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                {P.context.metrics.map((m, i) => (
                  <div key={i}>
                    <div className="text-[40px] font-extrabold leading-none text-gradient">{m.k}</div>
                    <div className="mt-2 text-[13.5px] font-bold text-white/95">{m.label}</div>
                    <div className="mt-1 text-[12px] text-white/60">{m.note}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-4 shadow-soft">
              <div className="relative aspect-video rounded-2xl bg-gradient-to-br from-royal via-purple to-cyan grain overflow-hidden flex items-center justify-center">
                <span className="absolute left-4 top-4 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-amber-700">Coming soon</span>
                <div className="text-center px-6">
                  <PlayCircle className="mx-auto h-14 w-14 text-white/95" />
                  <div className="mt-3 text-[12.5px] font-bold tracking-wide text-white">{P.context.mediaTitle}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================ LEARNING MODULE PORTFOLIO ================ */}
      <section className="px-6 py-16 bg-white" data-testid="partners-portfolio">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.portfolio.eyebrow} title={P.portfolio.title} desc={P.portfolio.desc} />

          <div className="mt-8">
            <div className="text-[11.5px] font-bold tracking-[0.14em] text-mint">{P.portfolio.availableLabel}</div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {P.portfolio.availableNow.map((m, i) => (
                <VideoCard key={i} chapter={m.subject} title={m.title} status="Available" gradient={['from-purple to-royal','from-royal to-cyan','from-cyan to-mint','from-mint to-royal'][i % 4]} />
              ))}
            </div>
          </div>

          <div className="mt-12">
            <div className="text-[11.5px] font-bold tracking-[0.14em] text-royal">{P.portfolio.upcomingLabel}</div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {P.portfolio.upcoming.map((m, i) => (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                  <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Upcoming</div>
                  <div className="mt-3 text-[14px] font-bold text-navy">{m.title}</div>
                  <div className="mt-1 text-[11.5px] font-bold tracking-[0.12em] text-purple">{m.subject.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================ CONTRIBUTION BOUNDARIES ================ */}
      <section className="px-6 py-16" data-testid="partners-boundaries">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={P.boundaries.eyebrow} title={P.boundaries.title} desc={P.boundaries.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-mint/30 bg-mint-50/60 p-6 shadow-soft">
              <div className="text-[11.5px] font-bold tracking-[0.14em] text-mint">INCLUDED</div>
              <div className="mt-2 text-[16px] font-bold text-navy">{P.boundaries.included.title}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{P.boundaries.included.items}</p>
            </div>
            <div className="rounded-2xl border border-royal/30 bg-royal-50/60 p-6 shadow-soft">
              <div className="text-[11.5px] font-bold tracking-[0.14em] text-royal">PROGRAMME-FUNDED</div>
              <div className="mt-2 text-[16px] font-bold text-navy">{P.boundaries.funded.title}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{P.boundaries.funded.items}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================ FINAL CTA ================ */}
      <section className="px-6 pb-24" data-testid="partners-cta">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={P.cta.title} primary={P.cta.primary} />
        </div>
      </section>

      {showcaseOpen && (
        <VideoLightbox
          videoId={FEATURED_VIDEO_ID}
          title="Gyan Dhara — A curriculum-aligned VR programme, in real classrooms"
          onClose={() => setShowcaseOpen(false)}
        />
      )}
    </PageShell>
  );
}
